import { useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Table, StatusPill, EmptyState, useToast } from '../../../design-system';

const fmtINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

// Turnover per FY drives the DERIVED audit-applicability flags the API returns
// as virtuals on the client (auditApplicability). We never store those flags —
// we just render what the model computed from the numbers below.
function TurnoverManager({ clientId, client, onClientChange }) {
  const toast = useToast();
  const [fy, setFy] = useState('');
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);

  const rows = client.turnoverByFY || [];
  const audit = client.auditApplicability || {};

  const persist = async (nextRows) => {
    setSaving(true);
    try {
      const res = await apiClient.put(`/api/clients/${clientId}`, { turnoverByFY: nextRows });
      onClientChange(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to save turnover');
    } finally {
      setSaving(false);
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}$/.test(fy.trim())) { toast.danger('FY must look like 2024-25'); return; }
    if (!amount || Number(amount) < 0) { toast.danger('Enter a valid amount'); return; }
    const others = rows.filter((r) => r.fy !== fy.trim());
    await persist([...others, { fy: fy.trim(), amount: Number(amount) }]);
    setFy(''); setAmount('');
  };

  const remove = async (targetFy) => {
    await persist(rows.filter((r) => r.fy !== targetFy));
  };

  const columns = [
    { key: 'fy', header: 'Financial Year', render: (r) => r.fy },
    { key: 'amount', header: 'Turnover', render: (r) => fmtINR(r.amount) },
    {
      key: 'actions', header: '', render: (r) => (
        <button className="btn-remove" onClick={() => remove(r.fy)} disabled={saving}>Remove</button>
      ),
    },
  ];

  return (
    <div className="tab-content">
      <Card>
        <Card.Header title="Turnover by Financial Year" />
        <form className="form-row" onSubmit={add} style={{ alignItems: 'flex-end' }}>
          <Input label="FY" placeholder="2024-25" value={fy} onChange={(e) => setFy(e.target.value)} />
          <Input label="Turnover (₹)" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Button type="submit" variant="primary" loading={saving}>Add / Update</Button>
        </form>
        {rows.length === 0 ? (
          <EmptyState icon="📈" title="No turnover recorded" description="Add a year's turnover to derive audit applicability." />
        ) : (
          <Table columns={columns} data={rows.slice().sort((a, b) => (a.fy < b.fy ? 1 : -1))} />
        )}
      </Card>

      <Card>
        <Card.Header title="Audit Applicability (derived)" />
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Tax Audit u/s 44AB</span>
            <StatusPill status={audit.taxAudit44AB ? 'active' : 'inactive'} label={audit.taxAudit44AB ? 'Applicable' : 'Not applicable'} />
          </div>
          <div className="info-item">
            <span className="info-label">GST Audit (GSTR-9C)</span>
            <StatusPill status={audit.gstAudit ? 'active' : 'inactive'} label={audit.gstAudit ? 'Applicable' : 'Not applicable'} />
          </div>
          <div className="info-item">
            <span className="info-label">Statutory Audit</span>
            <StatusPill status={audit.statutoryAudit ? 'active' : 'inactive'} label={audit.statutoryAudit ? 'Applicable' : 'Not applicable'} />
          </div>
        </div>
        <p className="hint-text">
          Derived from the latest turnover and client type — a prompt, not a legal determination
          (the ₹10cr digital-transactions 44AB relaxation is not modelled).
        </p>
      </Card>
    </div>
  );
}

export default TurnoverManager;
